(() => {
  // ponytail: document images only; embedded iframe and shadow-root apps need their own disclosures.
  const label = document.currentScript.dataset.aiImageryLabel;
  const images = new Map();
  let pending = false;
  const forget = (image, entry) => {
    if (entry.owned) entry.marker?.remove();
    else if (entry.marker) {
      entry.marker.classList.remove('ai-imagery-marker--small');
      entry.host.appendChild(entry.marker);
    }
    resize.unobserve(image);
    resize.unobserve(entry.host);
    image.removeEventListener('load', schedule);
    images.delete(image);
  };

  const update = () => {
    pending = false;
    for (const [image, entry] of images) {
      if (!image.isConnected) {
        forget(image, entry);
        continue;
      }
      if (!entry.marker) continue;
      if (entry.host.matches('.link-card-gallery__card')) {
        // Keep the disclosure attached to the card, independent of carousel clipping and image zoom.
        entry.marker.hidden = false;
        entry.marker.classList.remove('ai-imagery-marker--small');
        entry.marker.dataset.explanationSide = entry.position?.endsWith('L') ? 'right' : 'left';
        const captionHeight = entry.host.querySelector('.link-card-gallery__bar')?.offsetHeight || 0;
        Object.assign(entry.marker.style, { left: 'auto', top: 'auto', right: '12px', bottom: `${captionHeight + 12}px`, maxWidth: 'calc(100% - 24px)' });
        if (entry.position?.endsWith('L')) Object.assign(entry.marker.style, { left: '12px', right: 'auto' });
        if (entry.position?.startsWith('T')) Object.assign(entry.marker.style, { top: '12px', bottom: 'auto' });
        continue;
      }
      const bounds = image.getBoundingClientRect();
      const rect = { left: bounds.left, top: bounds.top, right: bounds.left + bounds.width, bottom: bounds.top + bounds.height };
      for (let ancestor = image.parentElement; ancestor && ancestor !== document.body; ancestor = ancestor.parentElement) {
        const style = getComputedStyle(ancestor);
        const clip = ancestor.getBoundingClientRect();
        if (/hidden|clip|scroll|auto/.test(style.overflowX)) {
          rect.left = Math.max(rect.left, clip.left);
          rect.right = Math.min(rect.right, clip.left + clip.width);
        }
        if (/hidden|clip|scroll|auto/.test(style.overflowY)) {
          rect.top = Math.max(rect.top, clip.top);
          rect.bottom = Math.min(rect.bottom, clip.top + clip.height);
        }
      }
      if (entry.host.matches('.hero-media__frame') && window.matchMedia('(max-width: 767.98px)').matches) {
        const card = image.closest('.overlay-text-parent--mobile-stack')?.querySelector('.overlay-text');
        if (card) rect.bottom = Math.min(rect.bottom, card.getBoundingClientRect().top);
      }
      rect.width = Math.max(0, rect.right - rect.left);
      rect.height = Math.max(0, rect.bottom - rect.top);
      const hostRect = entry.host.getBoundingClientRect();
      entry.marker.hidden = !rect.width || !rect.height;
      if (entry.marker.hidden) continue;
      const scaleX = hostRect.width / entry.host.offsetWidth || 1;
      const scaleY = hostRect.height / entry.host.offsetHeight || 1;
      const pdp = image.closest('.product-media--image');
      const mobilePdp = pdp && window.matchMedia('(max-width: 767.98px)').matches;
      const inset = image.closest('.product-block__image, .product-block') ? 10 : 12;
      const marker = entry.marker;
      const small = bounds.width < 150;
      const alignLeft = entry.position ? entry.position.endsWith('L') : !!pdp;
      const alignTop = entry.position ? entry.position.startsWith('T') : mobilePdp && !small;
      marker.classList.toggle('ai-imagery-marker--small', small);
      marker.dataset.explanationSide = alignLeft ? 'right' : 'left';
      marker.style.maxWidth = `${Math.max(0, rect.width / scaleX - 24)}px`;
      marker.style.right = 'auto';
      marker.style.bottom = 'auto';
      marker.style.left = `${(rect.left - hostRect.left) / scaleX - entry.host.clientLeft + entry.host.scrollLeft + (alignLeft ? (small ? 6 : inset) : rect.width / scaleX - marker.offsetWidth - (small ? 6 : inset))}px`;
      marker.style.top = `${(rect.top - hostRect.top) / scaleY - entry.host.clientTop + entry.host.scrollTop + (alignTop ? (small ? 6 : inset) : rect.height / scaleY - marker.offsetHeight - (small ? 6 : inset))}px`;
    }
  };
  const schedule = () => {
    if (!pending) {
      pending = true;
      requestAnimationFrame(update);
    }
  };
  const resize = new ResizeObserver(schedule);

  const inspect = (image, altChanged = false) => {
    const frame = image.closest('[data-ai-alt-desktop]');
    const sourceAlt = frame?.getAttribute(window.matchMedia('(max-width: 767.98px)').matches ? 'data-ai-alt-mobile' : 'data-ai-alt-desktop');
    const imageAlt = image.getAttribute('alt') || '';
    const rendererFlag = image.closest('[data-ai-imagery="true"]');
    const alt = sourceAlt ?? (rendererFlag ? `[AI] ${imageAlt}` : imageAlt);
    const previous = images.get(image);
    if (previous && (sourceAlt != null ? alt === previous.sourceAlt : !altChanged && (alt === previous.alt || alt === previous.sourceAlt))) return;
    if (previous) forget(image, previous);
    if (!alt.includes('[AI]')) {
      if (sourceAlt != null && image.getAttribute('alt') !== alt) image.setAttribute('alt', alt);
      return;
    }
    const position = alt.match(/\[(TL|TR|BL|BR)\]/)?.[1];
    const cleanAlt = alt.replace(/\[(AI|TL|TR|BL|BR)\]/g, '').trim();
    const host = image.closest('.rimage-wrapper, .hero-media__frame, .link-card-gallery__card') || (image.parentElement.tagName === 'PICTURE' ? image.parentElement.parentElement : image.parentElement);
    const existing = host.querySelector(':scope > .ai-imagery-marker');
    let marker = existing || null;
    if (!existing || !host.matches('.rimage-wrapper, .hero-media__frame, .link-card-gallery__card')) {
      marker = document.createElement('span');
      marker.className = 'ai-imagery-marker';
      marker.setAttribute('role', 'button');
      marker.setAttribute('tabindex', '0');
      marker.setAttribute('aria-expanded', 'false');
      marker.setAttribute('aria-label', label);
      const icon = document.createElement('span');
      icon.className = 'ai-imagery-marker__icon';
      icon.setAttribute('aria-hidden', 'true');
      marker.appendChild(icon);
      host.classList.add('ai-imagery-host');
      host.appendChild(marker);
    }
    resize.observe(image);
    resize.observe(host);
    image.addEventListener('load', schedule);
    images.set(image, { alt: cleanAlt, sourceAlt: alt, marker, host, position, owned: marker !== existing });
    if (image.getAttribute('alt') !== cleanAlt) image.setAttribute('alt', cleanAlt);
    schedule();
  };
  const scan = (node) => {
    if (node.nodeType !== 1) return;
    if (node.matches('img')) inspect(node);
    node.querySelectorAll('img').forEach((image) => inspect(image));
  };
  let activeMarker;
  let tooltip;
  let pinned = false;
  const close = () => {
    activeMarker?.setAttribute('aria-expanded', 'false');
    activeMarker = null;
    pinned = false;
    if (tooltip) tooltip.hidden = true;
  };
  const show = (marker) => {
    close();
    if (!tooltip) {
      tooltip = document.createElement('div');
      tooltip.className = 'ai-imagery-tooltip';
      tooltip.setAttribute('role', 'tooltip');
      document.body.appendChild(tooltip);
    }
    activeMarker = marker;
    marker.setAttribute('aria-expanded', 'true');
    tooltip.textContent = marker.getAttribute('aria-label') || label;
    tooltip.hidden = false;
    const rect = marker.getBoundingClientRect();
    const right = rect.left + rect.width + 8;
    const left = rect.left - tooltip.offsetWidth - 8;
    const preferLeft = (marker.dataset.explanationSide || (marker.closest('.product-media--image') ? 'right' : 'left')) === 'left';
    const preferred = preferLeft ? left : right;
    const alternate = preferLeft ? right : left;
    const fits = (position) => position >= 8 && position + tooltip.offsetWidth <= window.innerWidth - 8;
    tooltip.style.left = `${Math.max(8, Math.min(fits(preferred) ? preferred : alternate, window.innerWidth - tooltip.offsetWidth - 8))}px`;
    tooltip.style.top = `${Math.max(8, Math.min(rect.top + (rect.height - tooltip.offsetHeight) / 2, window.innerHeight - tooltip.offsetHeight - 8))}px`;
  };
  // Markers can live inside image links: consume activation so disclosure never navigates.
  document.addEventListener('click', (event) => {
    const marker = event.target.closest('.ai-imagery-marker');
    if (!marker) { close(); return; }
    event.preventDefault();
    event.stopPropagation();
    if (activeMarker === marker && pinned) close();
    else { show(marker); pinned = true; }
  }, true);
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') { close(); return; }
    const marker = event.target.closest('.ai-imagery-marker');
    if (marker && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      event.stopPropagation();
      if (activeMarker === marker && pinned) close();
      else { show(marker); pinned = true; }
    }
  }, true);
  document.addEventListener('pointerover', (event) => {
    if (event.pointerType === 'touch') return;
    const marker = event.target.closest('.ai-imagery-marker');
    if (marker && marker !== activeMarker) show(marker);
  });
  document.addEventListener('pointerout', (event) => {
    if (event.pointerType === 'touch' || pinned) return;
    if (event.relatedTarget?.closest('.ai-imagery-marker, .ai-imagery-tooltip')) return;
    if (event.target.closest('.ai-imagery-marker') === activeMarker || event.target.closest('.ai-imagery-tooltip')) close();
  });
  document.addEventListener('focusin', (event) => {
    const marker = event.target.closest('.ai-imagery-marker');
    if (marker) show(marker);
  });
  document.addEventListener('focusout', close);
  window.addEventListener('scroll', () => { close(); schedule(); }, true);
  scan(document.documentElement);
  new MutationObserver((changes) => {
    for (const change of changes) {
      if (change.type === 'attributes') inspect(change.target, !(change.oldValue || '').includes('[AI]'));
      else change.addedNodes.forEach(scan);
    }
    schedule();
  }).observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeOldValue: true, attributeFilter: ['alt'] });
  window.matchMedia('(max-width: 767.98px)').addEventListener('change', () => {
    document.querySelectorAll('[data-ai-alt-desktop] img').forEach((image) => inspect(image));
    schedule();
  });
  window.addEventListener('resize', schedule);
})();
